import { default as Col } from 'antd/es/col';
import { default as InputNumber } from 'antd/es/input-number';
import { default as Row } from 'antd/es/row';
import { default as Select } from 'antd/es/select';
import { default as Space } from 'antd/es/space';
import { default as TreeSelect } from 'antd/es/tree-select';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { filterOption } from 'utils/antd/filters-option';
import { formatDate } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';
import { normalize } from 'utils/normalize';

import {
  useGetCustomerBusinessUnits,
  useGetDisbursementPaymentInstruments,
  useGetDisbursementStatsPayoutRequestsReasonCodes
} from 'api';
import type { PayoutReasonCodeStats } from 'api';

import type { LocalState } from 'reduxLocal/slices/pages.payoutsSlice';

import { useContractedProducts, useOrganisationId } from 'hooks/context';
import { useCurrencyLabel } from 'hooks/useCurrencyLabel';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { LocalDateRangeWrapper } from '@molecules/LocalDateRangeWrapper';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import {
  PAYOUTS_ACCOUNT_FILTER_TEST_ID,
  PAYOUTS_AMOUNT_FILTER_TEST_ID,
  PAYOUTS_AMOUNT_FROM_FILTER_TEST_ID,
  PAYOUTS_AMOUNT_TO_FILTER_TEST_ID,
  PAYOUTS_APPLY_AMOUNT_BUTTON_TEST_ID,
  PAYOUTS_CLEAR_FILTERS_BUTTON_TEST_ID,
  PAYOUTS_CURRENCY_FILTER_TEST_ID,
  PAYOUTS_STATUS_FILTER_TEST_ID
} from './constants';
import { useLocalState } from './localState';
import type { ReasonCodeNodeValue } from './util';
import {
  isReasonCodeNodeValue,
  mapToTreeData,
  renderAmountFromAndTo
} from './util';

const { Option } = Select;

const STATUS_OR_REASON_CODE_OPTIONS = {
  ALL: 'All'
} as const;

export const PayoutFilters = () => {
  const {
    state: { filters },
    resetState,
    updateFilters,
    updateCursorBefore,
    updateCursorAfter
  } = useLocalState();

  const setFilters = useCallback(
    (dispatch: (filters: LocalState['filters']) => LocalState['filters']) => {
      updateFilters(dispatch(filters));
      updateCursorBefore(undefined);
      updateCursorAfter(undefined);
    },
    [filters, updateCursorAfter, updateCursorBefore, updateFilters]
  );

  /**
   * Helpers
   */
  const currencyLabel = useCurrencyLabel();

  /**
   * State
   */

  const [amountFrom, setAmountFrom] = useState<string>();
  const [amountTo, setAmountTo] = useState<string>();
  const [stateOrReasonCode, setStateOrReasonCode] = useState<string>(
    STATUS_OR_REASON_CODE_OPTIONS.ALL
  );

  const [searchParams] = useSearchParams();

  useEffect(() => {
    setStateOrReasonCode(
      searchParams.get('state') ?? STATUS_OR_REASON_CODE_OPTIONS.ALL
    );
  }, [searchParams]);

  const [reasonCodes, setReasonCodes] = useState<PayoutReasonCodeStats>();
  const organisationId = useOrganisationId<number>();

  const { filters: globalFilters } = useGlobalFilters();

  /**
   * Data fetching
   */

  const { data: _reasonCodes } =
    useGetDisbursementStatsPayoutRequestsReasonCodes(organisationId, {
      ...(hasValue(globalFilters?.dateRange?.from) && {
        created_after: formatDate(globalFilters.dateRange.from, 'utc', 'iso')
      }),
      ...(hasValue(globalFilters?.dateRange?.to) && {
        created_before: formatDate(globalFilters.dateRange.to, 'utc', 'iso')
      })
    });

  // Cache currently seen reason codes by merging objects
  useEffect(() => {
    setReasonCodes((prev) =>
      _.mapValues(_.merge({}, prev, _reasonCodes?.data), (key) => ({
        items: _.sortBy(_.uniqBy(key.items, 'id'), 'value'),
        meta: key.meta
      }))
    );
  }, [_reasonCodes]);

  const { contractedProducts } = useContractedProducts();

  const { data: businessUnits, isLoading: isBusinessUnitsLoading } =
    useGetCustomerBusinessUnits(organisationId);

  const { data: { data: paymentInstruments } = {} } =
    useGetDisbursementPaymentInstruments(organisationId);

  const uniqueCurrencies = useMemo(() => {
    return [
      ...new Set(
        contractedProducts?.data?.payout_products?.map(
          (product) => product.product_currency_iso
        )
      )
    ].map((iso) => ({ iso, name: currencyLabel.fromCode(iso) }));
  }, [contractedProducts?.data?.payout_products, currencyLabel]);

  /**
   * Effects
   */

  useEffect(() => {
    setAmountFrom(filters.amountFrom);
    setAmountTo(filters.amountTo);
  }, [filters.amountFrom, filters.amountTo]);

  /**
   * Memos
   */
  const treeData = useMemo(() => {
    if (hasValue(reasonCodes)) {
      return [
        // add the All to be the first option alongside the rest of the other reason codes that we get from the Backend
        {
          key: 0,
          title: 'All Statuses',
          value: STATUS_OR_REASON_CODE_OPTIONS.ALL,
          status: undefined
        },
        ...mapToTreeData(reasonCodes)
      ];
    }
  }, [reasonCodes]);

  /**
   * callbacks
   */
  const onStateOrReasonCodeChange = useCallback(
    (_: string, node: ReasonCodeNodeValue) => {
      if (stateOrReasonCode === node.value) {
        return;
      }

      // user wants everything
      if (node.value === STATUS_OR_REASON_CODE_OPTIONS.ALL) {
        setFilters((filters) => ({
          ...filters,
          state: undefined,
          reasonCode: undefined
        }));
      } else {
        // user wants to fetch specific reason code or specific reason
        setFilters((filters) => ({
          ...filters,
          state: node.status,
          reasonCode: node.reasonCode
        }));
      }

      setStateOrReasonCode(node.value);
    },
    [setFilters, stateOrReasonCode]
  );

  const hasFilters = useMemo(() => {
    return Object.values(filters).some(
      (value) => Boolean(value) && value !== STATUS_OR_REASON_CODE_OPTIONS.ALL
    );
  }, [filters]);

  return (
    <div className="flex items-center">
      <Space wrap size="small">
        <LocalDateRangeWrapper />
        <SelectVirtualAccount
          onChangeCallback={(value) => {
            setFilters((filters) => ({
              ...filters,
              accountNumber: value
            }));
          }}
          currentAccountNumber={filters.accountNumber}
          e2eId={PAYOUTS_ACCOUNT_FILTER_TEST_ID}
          filterCryptoAccounts={true}
        />

        {/* Currency filtering dropdown */}
        <Select
          className="w-40"
          showSearch
          filterOption={filterOption}
          value={filters.currency}
          popupMatchSelectWidth={false}
          data-testid={PAYOUTS_CURRENCY_FILTER_TEST_ID}
          defaultValue={STATUS_OR_REASON_CODE_OPTIONS.ALL}
          onChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              currency: value
            }));
          }}
        >
          <Option
            key={STATUS_OR_REASON_CODE_OPTIONS.ALL}
            index={STATUS_OR_REASON_CODE_OPTIONS.ALL}
          >
            All Currencies
          </Option>
          {uniqueCurrencies.map((currency) => (
            <Option
              index={`${currency.name}-${currency.iso}`}
              key={currency.iso}
            >
              <div className="flex flex-row items-center gap-2">
                <CountryFlag
                  className="!size-[22px]"
                  countryCode={getCountryCode(currency.iso ?? null)}
                  svg
                />
                {currency.name}
              </div>
            </Option>
          ))}
        </Select>

        {/* Amount filtering dropdown with slider */}
        <Select
          className="w-40"
          data-testid={PAYOUTS_AMOUNT_FILTER_TEST_ID}
          value={renderAmountFromAndTo(amountFrom, amountTo)}
          styles={{ popup: { root: { width: 300, padding: 15 } } }}
          popupRender={() => (
            <div>
              <Row className="items-center justify-between">
                <Col>
                  <InputNumber
                    data-testid={PAYOUTS_AMOUNT_FROM_FILTER_TEST_ID}
                    min="0"
                    placeholder="From"
                    value={amountFrom}
                    onChange={(newValue) => {
                      if (newValue !== null) {
                        setAmountFrom(newValue);
                      }
                    }}
                  />
                </Col>
                <Col>
                  <InputNumber
                    data-testid={PAYOUTS_AMOUNT_TO_FILTER_TEST_ID}
                    placeholder="To"
                    min="0"
                    value={amountTo}
                    onChange={(newValue) => {
                      if (newValue !== null) {
                        setAmountTo(newValue);
                      }
                    }}
                  />
                </Col>
                <TertiaryButton
                  onClick={() => {
                    if (
                      filters.amountFrom !== undefined ||
                      filters.amountTo !== undefined
                    ) {
                      updateFilters({
                        ...filters,
                        amountFrom: undefined,
                        amountTo: undefined
                      });
                    }
                  }}
                >
                  Reset
                </TertiaryButton>
              </Row>
              <Col className="mt-2">
                <PrimaryButton
                  data-testid={PAYOUTS_APPLY_AMOUNT_BUTTON_TEST_ID}
                  className="w-full"
                  onClick={() => {
                    let newFilters = {
                      ...filters,
                      amountFrom: amountFrom?.toString(),
                      amountTo: amountTo?.toString()
                    };
                    if (
                      newFilters.amountTo !== filters.amountTo ||
                      newFilters.amountFrom !== filters.amountFrom
                    ) {
                      updateFilters(newFilters);
                    }
                  }}
                >
                  Apply
                </PrimaryButton>
              </Col>
            </div>
          )}
        />

        <TreeSelect
          className="w-40"
          showSearch
          filterTreeNode={(input, node) => {
            return normalize(node.title as string).includes(normalize(input));
          }}
          data-testid={PAYOUTS_STATUS_FILTER_TEST_ID}
          value={stateOrReasonCode}
          popupMatchSelectWidth={false}
          treeData={treeData}
          placeholder="Please select"
          onSelect={(value, node) => {
            if (isReasonCodeNodeValue(node)) {
              onStateOrReasonCodeChange(value, node);
            }
          }}
        />
        <>
          <Select
            className="w-40"
            showSearch
            optionFilterProp="children"
            defaultValue={STATUS_OR_REASON_CODE_OPTIONS.ALL}
            loading={isBusinessUnitsLoading}
            data-testid="PAYOUTS_BUSINESS_UNIT_FILTER_TEST_ID"
            value={filters.businessUnitId}
            onChange={(value) => {
              setFilters((filters) => ({
                ...filters,
                businessUnitId: value
              }));
            }}
          >
            <Option key={STATUS_OR_REASON_CODE_OPTIONS.ALL}>
              All Business Units
            </Option>
            {businessUnits?.data?.map((unit) => (
              <Option key={unit.name} value={unit.id}>
                {unit.name}
              </Option>
            ))}
          </Select>

          {paymentInstruments && (
            <Select
              className="w-40"
              showSearch
              optionFilterProp="children"
              defaultValue={STATUS_OR_REASON_CODE_OPTIONS.ALL}
              loading={false}
              data-testid="PAYOUTS_LOCAL_INSTRUMENT_FILTER_TEST_ID"
              value={filters.localInstrument}
              onChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  localInstrument: value
                }));
              }}
            >
              <Option key={STATUS_OR_REASON_CODE_OPTIONS.ALL}>
                All Transfer Types
              </Option>
              {paymentInstruments.map((instrument) => (
                <Option
                  key={instrument.local_instrument_family}
                  value={instrument.local_instrument_family.toLowerCase()}
                >
                  {instrument.local_instrument_family}
                </Option>
              ))}
            </Select>
          )}
        </>

        {hasFilters && (
          <TertiaryButton
            data-testid={PAYOUTS_CLEAR_FILTERS_BUTTON_TEST_ID}
            className="h-10 font-semibold"
            onClick={() => {
              setStateOrReasonCode(STATUS_OR_REASON_CODE_OPTIONS.ALL);
              resetState();
            }}
          >
            Clear filters
          </TertiaryButton>
        )}
      </Space>
    </div>
  );
};
